import React, { lazy } from "react";
import * as Sentry from "@sentry/react";
import {
  Navigate,
  BrowserRouter as Router,
  useLocation,
  useRoutes,
} from "react-router-dom";
import { DndProvider } from "react-dnd";
import { MultiBackend } from "react-dnd-multi-backend";
import { HTML5toTouchCustom } from "./utility/dndBackends";
import AppShell from "./AppShell";
import { AuthProvider, useAuthContext } from "./context/AuthContext";
import TranslationProvider from "./context/Internationalization";
import { SnackBarProvider } from "./context/SnackBarContext";
import { GoogleMapsProvider } from "./context/GoogleMapsContext";

import Club from "./pages/Club/Club";
import Login from "./pages/Login/Login";
import MagicLinkHandler from "./pages/Login/MagicLinkHandler";
import SuperAdminClubs from "./pages/SuperAdmin/Clubs/SuperAdminClubs";
import SuperAdminProvider from "./context/SuperAdminContext";
import OfficialLeagues from "./pages/SuperAdmin/OfficialLeagues/OfficialLeagues";
import SuperAdminFields from "./pages/SuperAdmin/Fields/SuperAdminFields";
import SuperAdminBanReports from "./pages/SuperAdmin/BanReports/SuperAdminBanReports";

const Home = lazy(() => import("./pages/Home/Home"));

// Create a patched useRoutes hook ONCE (outside of any component)
const useRoutesWithSentry = Sentry.wrapUseRoutesV7(useRoutes);

// Small guard that waits for auth, then enforces admin
function RequireAdmin({ children }) {
  const auth = useAuthContext();
  const { user, initialized } = auth.authState || {};
  const location = useLocation();

  if (!initialized && typeof user === "undefined") return null;

  if (!auth.isAuthenticated() || !user?.roles?.includes("admin")) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}

function AppRoutes() {
  const routes = [
    { path: "/login", element: <Login /> },
    { path: "/", element: <MagicLinkHandler /> },

    // club area
    {
      path: "/club/*",
      element: (
        <RequireAdmin>
          <AppShell>
            <Club />
          </AppShell>
        </RequireAdmin>
      ),
    },

    // explicit super-admin pages (no greedy /*)
    {
      path: "/super-admin/clubs/*",
      element: (
        <RequireAdmin>
          <AppShell>
            <SuperAdminClubs />
          </AppShell>
        </RequireAdmin>
      ),
    },
    {
      path: "/super-admin/stories/*",
      element: (
        <RequireAdmin>
          <AppShell>
            <Home />
          </AppShell>
        </RequireAdmin>
      ),
    },
    {
      path: "/super-admin/official-leagues/*",
      element: (
        <RequireAdmin>
          <AppShell>
            <OfficialLeagues />
          </AppShell>
        </RequireAdmin>
      ),
    },
    {
      path: "/super-admin/fields/*",
      element: (
        <RequireAdmin>
          <AppShell>
            <SuperAdminFields />
          </AppShell>
        </RequireAdmin>
      ),
    },
    {
      path: "/super-admin/ban-reports/*",
      element: (
        <RequireAdmin>
          <AppShell>
            <SuperAdminBanReports />
          </AppShell>
        </RequireAdmin>
      ),
    },

    // final catch-all → login (or choose a dashboard)
    { path: "*", element: <Navigate to="/login" replace /> },
  ];

  // Use the Sentry-instrumented hook
  return useRoutesWithSentry(routes);
}

function App() {
  return (
    <SuperAdminProvider isUserSuperAdmin={true}>
      <AppRoutes />
    </SuperAdminProvider>
  );
}

const AppWrapper = () => (
  <Router>
    <div className="Container">
      <AuthProvider>
        <GoogleMapsProvider>
          <TranslationProvider>
            <SnackBarProvider>
              <DndProvider backend={MultiBackend} options={HTML5toTouchCustom}>
                <App />
              </DndProvider>
            </SnackBarProvider>
          </TranslationProvider>
        </GoogleMapsProvider>
      </AuthProvider>
    </div>
  </Router>
);

export default AppWrapper;
